import React from "react";
import { queryTypeInfo } from "@/lib/format";

export function StatusBadge({ status }) {
  const map = {
    new: "bg-[#002FA7] text-white",
    contacted: "bg-[#FFCC00] text-gray-900",
    qualified: "bg-gray-900 text-white",
    converted: "bg-[#008A00] text-white",
    lost: "bg-[#E60000] text-white",
  };
  const cls = map[status] || "bg-gray-200 text-gray-900";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`}
      data-testid={`status-badge-${status}`}>
      {status || "unknown"}
    </span>
  );
}

export function SourceBadge({ source }) {
  const map = {
    IndiaMART: "border-[#002FA7] text-[#002FA7]",
    ExportersIndia: "border-[#BE185D] text-[#BE185D]",
    TradeIndia: "border-[#0E7490] text-[#0E7490]",
    Justdial: "border-[#E60000] text-[#E60000]",
    Manual: "border-gray-500 text-gray-700",
    WhatsApp: "border-[#008A00] text-[#008A00]",
    Website: "border-[#1E3A2A] text-[#1E3A2A]",
    Export: "border-[#B8860B] text-[#8B6508]",
  };
  const cls = map[source] || "border-gray-300 text-gray-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border ${cls} bg-white`}
      data-testid={`source-badge-${source}`}>
      {source}
    </span>
  );
}

export function QueryTypeBadge({ code, compact = false }) {
  const info = queryTypeInfo(code);
  if (!info) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${info.color}`}
      title={info.label}
      data-testid={`query-type-badge-${code}`}>
      {compact ? info.short : info.label}
    </span>
  );
}

// Unified enquiry-type badge that works for both IndiaMART (single-char QUERY_TYPE like W/P/B)
// and free-text enquiry_type fields ("direct", "buyleads", etc) from ExportersIndia and others.
const FREE_TYPE_COLORS = {
  direct: "bg-[#F0F4FF] text-[#002FA7]",
  buyleads: "bg-[#FEF3C7] text-[#92400E]",
  inquiry: "bg-[#F0F9FF] text-[#0891B2]",
  catalog: "bg-[#ECFDF5] text-[#008A00]",
};

// International team lead marker; the channel shows where it came from
// (Google Maps scraper / typed in manually on the International page).
const INTL_CHANNEL = { scraper: "Scraper", manual: "Manual" };
export function IntlBadge({ lead }) {
  if (!lead?.international) return null;
  const ch = INTL_CHANNEL[lead.intl_channel];
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-[#0F766E] text-white"
      title="International team lead" data-testid={`intl-badge-${lead.intl_channel || "x"}`}>
      Intl{ch ? ` · ${ch}` : ""}
    </span>
  );
}

// Lead tag/label chip (Fragvansh labels feature)
export function TagBadge({ tag, onRemove }) {
  if (!tag) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-[#7C3AED] text-[#7C3AED] bg-[#F5F3FF]"
      data-testid={`tag-badge-${tag}`}>
      {tag}
      {onRemove && (
        <button onClick={onRemove} className="hover:text-[#4C1D95] leading-none" title={`Remove tag ${tag}`}
          data-testid={`tag-remove-${tag}`}>×</button>
      )}
    </span>
  );
}

// "Also in <other company>" note — the same phone exists on the other company's
// book. Informational only; the leads stay fully independent.
const COMPANY_LABELS = { citspray: "CitSpray", fragvansh: "Fragvansh" };
export function AlsoInBadge({ lead }) {
  const others = (lead?.also_in || []).filter((c) => COMPANY_LABELS[c]);
  if (!others.length) return null;
  return (
    <>
      {others.map((c) => (
        <span key={c}
          className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-dashed border-amber-500 text-amber-700 bg-amber-50"
          title={`This customer also exists in ${COMPANY_LABELS[c]}`}
          data-testid={`also-in-badge-${c}`}>
          also in {COMPANY_LABELS[c]}
        </span>
      ))}
    </>
  );
}

export function EnquiryTypeBadge({ lead }) {
  if (!lead) return null;
  // Prefer the explicit free-text enquiry_type, then fall back to IndiaMART's QUERY_TYPE code.
  const freeText = (lead.enquiry_type || "").trim();
  if (freeText) {
    // IndiaMART leads store the raw QUERY_TYPE code (W/B/P/BIZ/WA) into enquiry_type.
    // Map these to human-readable labels with proper colors.
    const mapped = queryTypeInfo(freeText);
    if (mapped && mapped.label !== freeText) {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${mapped.color}`}
          title={mapped.label}
          data-testid={`enquiry-type-badge-${freeText.toLowerCase()}`}>
          {mapped.label}
        </span>
      );
    }
    // Free-text enquiry types (ExportersIndia, etc.)
    const key = freeText.toLowerCase().replace(/[^a-z]/g, "");
    const cls = FREE_TYPE_COLORS[key] || "bg-gray-100 text-gray-800";
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${cls}`}
        title={`Enquiry type: ${freeText}`}
        data-testid={`enquiry-type-badge-${key || "generic"}`}>
        {freeText}
      </span>
    );
  }
  const code = lead.source_data?.QUERY_TYPE;
  return <QueryTypeBadge code={code} compact />;
}
